import { useEffect, useRef, useState, useCallback } from 'react';
import { MdLocationOn, MdClose } from 'react-icons/md';
import { HiOutlineSearch } from 'react-icons/hi';

// ─── Self-contained Google Maps types (no @types/google.maps needed) ─────────

interface GeocoderAddressComponent {
  long_name:  string;
  short_name: string;
  types:      string[];
}

interface GeocoderGeometry {
  location: { lat: () => number; lng: () => number };
}

interface GeocoderResult {
  address_components: GeocoderAddressComponent[];
  geometry:           GeocoderGeometry;
  formatted_address:  string;
}

interface AutocompletePrediction {
  place_id:             string;
  description:          string;
  structured_formatting: {
    main_text:      string;
    secondary_text: string;
  };
}

type PlacesServiceStatusType =
  | 'OK' | 'ZERO_RESULTS' | 'INVALID_REQUEST'
  | 'OVER_QUERY_LIMIT' | 'REQUEST_DENIED' | 'UNKNOWN_ERROR';

type GeocoderStatusType =
  | 'OK' | 'ZERO_RESULTS' | 'OVER_DAILY_LIMIT'
  | 'OVER_QUERY_LIMIT' | 'REQUEST_DENIED' | 'INVALID_REQUEST' | 'UNKNOWN_ERROR';

interface GMapsAutocompleteService {
  getPlacePredictions(
    request:  { input: string; sessionToken?: unknown },
    callback: (predictions: AutocompletePrediction[] | null, status: PlacesServiceStatusType) => void
  ): void;
}

interface GMapsGeocoder {
  geocode(
    request:  { placeId: string },
    callback: (results: GeocoderResult[] | null, status: GeocoderStatusType) => void
  ): void;
}

declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteService:      new () => GMapsAutocompleteService;
          AutocompleteSessionToken: new () => unknown;
          PlacesServiceStatus:      Record<string, PlacesServiceStatusType>;
        };
        Geocoder:       new () => GMapsGeocoder;
        GeocoderStatus: Record<string, GeocoderStatusType>;
      };
    };
    initGoogleMapsCallback?: () => void;
  }
}

// ─── Public types ─────────────────────────────────────────────────────────────

export interface PlaceResult {
  /** Street-level address (street number + route + sublocality) */
  address:   string;
  /** Full formatted address from Google — use this to display in the input */
  formattedAddress: string;
  city:      string;
  state:     string;
  country:   string;
  pinCode:   string;
  latitude:  number;
  longitude: number;
}

export interface GooglePlaceAutoCompleteProps {
  apiKey:       string;
  /** Pass formattedAddress here to correctly pre-fill on edit */
  value?:       string;
  placeholder?: string;
  onSelect:     (result: PlaceResult) => void;
  className?:   string;
  required?:    boolean;
  label?:       string;
}

interface Suggestion {
  placeId:       string;
  description:   string;
  mainText:      string;
  secondaryText: string;
}

// ─── Script loader singleton ──────────────────────────────────────────────────

let scriptLoaded  = false;
let scriptLoading = false;
const pendingCallbacks: (() => void)[] = [];

function loadGoogleMapsScript(apiKey: string, callback: () => void): void {
  if (scriptLoaded && window.google?.maps?.places) { callback(); return; }
  pendingCallbacks.push(callback);
  if (scriptLoading) return;
  scriptLoading = true;

  window.initGoogleMapsCallback = () => {
    scriptLoaded  = true;
    scriptLoading = false;
    pendingCallbacks.forEach((cb) => cb());
    pendingCallbacks.length = 0;
  };

  const script   = document.createElement('script');
  script.src     = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMapsCallback`;
  script.async   = true;
  script.defer   = true;
  script.onerror = () => { scriptLoading = false; };
  document.head.appendChild(script);
}

// ─── Address component helpers ────────────────────────────────────────────────

function extractComponent(components: GeocoderAddressComponent[], type: string): string {
  return components.find((c) => c.types.includes(type))?.long_name || '';
}

function buildStreetAddress(components: GeocoderAddressComponent[]): string {
  const streetNumber = extractComponent(components, 'street_number');
  const route        = extractComponent(components, 'route');
  const sublocality  = extractComponent(components, 'sublocality_level_1');
  return [streetNumber, route, sublocality].filter(Boolean).join(', ');
}

// ─── Component ────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;

export default function GooglePlaceAutoComplete({
  apiKey,
  value       = '',
  placeholder = 'Search address...',
  onSelect,
  className   = '',
  required    = false,
  label,
}: GooglePlaceAutoCompleteProps) {
  const [inputValue,  setInputValue]  = useState<string>(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open,        setOpen]        = useState<boolean>(false);
  const [loading,     setLoading]     = useState<boolean>(false);
  const [apiReady,    setApiReady]    = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const serviceRef   = useRef<GMapsAutocompleteService | null>(null);
  const geocoderRef  = useRef<GMapsGeocoder | null>(null);
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef   = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const sessionToken = useRef<unknown>(null);

  // ── Load SDK ──
  useEffect(() => {
    loadGoogleMapsScript(apiKey, () => {
      serviceRef.current   = new window.google.maps.places.AutocompleteService();
      geocoderRef.current  = new window.google.maps.Geocoder();
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      setApiReady(true);
    });
  }, [apiKey]);

  // ── KEY FIX 1: Sync external value into input ──
  // When form loads saved data (edit mode), `value` prop is the formattedAddress
  // stored from the previous selection. We mirror it directly into the input.
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // ── Close on outside click ──
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Fetch suggestions (debounced) ──
  const fetchSuggestions = useCallback((query: string) => {
    if (!apiReady || !serviceRef.current || query.length < 2) {
      setSuggestions([]); setOpen(false); return;
    }
    setLoading(true);
    serviceRef.current.getPlacePredictions(
      { input: query, sessionToken: sessionToken.current },
      (predictions, status) => {
        setLoading(false);
        if (status === 'OK' && predictions) {
          setSuggestions(predictions.map((p) => ({
            placeId:       p.place_id,
            description:   p.description,
            mainText:      p.structured_formatting.main_text,
            secondaryText: p.structured_formatting.secondary_text,
          })));
          setOpen(true);
          setActiveIndex(-1);
        } else {
          setSuggestions([]); setOpen(false);
        }
      }
    );
  }, [apiReady]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => fetchSuggestions(val), DEBOUNCE_MS);
  };

  // ── Geocode and return result ──
  const handleSelect = useCallback((suggestion: Suggestion) => {
    // KEY FIX 2: Show the full description (e.g. "MG Road, Gurgaon, Haryana, India")
    // in the input — NOT just mainText — so the displayed value matches what
    // gets stored and re-populated on edit.
    setInputValue(suggestion.description);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);

    if (!geocoderRef.current) return;

    geocoderRef.current.geocode({ placeId: suggestion.placeId }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const r          = results[0];
        const components = r.address_components;

        const city =
          extractComponent(components, 'locality') ||
          extractComponent(components, 'administrative_area_level_2') ||
          extractComponent(components, 'postal_town');

        const streetAddress = buildStreetAddress(components);

        const result: PlaceResult = {
          // Short address (street only) — use for address field in your form
          address:           suggestion.description,
          formattedAddress: suggestion.description,
          city,
          state:            extractComponent(components, 'administrative_area_level_1'),
          country:          extractComponent(components, 'country'),
          pinCode:          extractComponent(components, 'postal_code'),
          latitude:         r.geometry.location.lat(),
          longitude:        r.geometry.location.lng(),
        };
        console.log(result,"result")

        // Refresh session token after each selection (billing best practice)
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();

        onSelect(result);
      }
    });
  }, [onSelect]);

  // ── Keyboard navigation ──
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false); setActiveIndex(-1);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  // ── Highlight matched text ──
  const highlightMatch = (text: string, query: string) => {
    if (!query) return <>{text}</>;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return <>{text}</>;
    return (
      <>
        {text.slice(0, idx)}
        <strong className="text-[#CC9900] font-semibold">
          {text.slice(idx, idx + query.length)}
        </strong>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`}>

      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* ── Input ── */}
      <div className="relative flex items-center">
        <HiOutlineSearch className="absolute left-3 text-[#858585] text-base pointer-events-none" />

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="relative  bg-[#F7F7F7] w-full rounded-lg h-10 focus:outline focus:outline-[1px] focus:outline-[#e5e7eb] focus:ring-0 px-3 pr-10 disabled:cursor-not-allowed"
        />

        {/* Spinner */}
        {loading && (
          <span className="absolute right-3 flex items-center">
            <svg className="animate-spin w-4 h-4 text-[#858585]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </span>
        )}

        {/* Clear */}
        {!loading && inputValue && (
          <button type="button" onClick={handleClear} tabIndex={-1}
            className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors">
            <MdClose className="text-base" />
          </button>
        )}
      </div>

      {/* ── Suggestions dropdown ── */}
      {open && suggestions.length > 0 && (
        <ul role="listbox"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          style={{ maxHeight: '260px', overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <li key={s.placeId} role="option" aria-selected={i === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={[
                'flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors text-sm',
                i === activeIndex ? 'bg-[#CC990012]' : 'hover:bg-gray-50',
                i !== suggestions.length - 1 ? 'border-b border-gray-50' : '',
              ].join(' ')}>
              <MdLocationOn className="text-[#CC9900] text-base mt-0.5 flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="text-gray-800 font-medium truncate">
                  {highlightMatch(s.mainText, inputValue)}
                </span>
                {s.secondaryText && (
                  <span className="text-gray-400 text-xs truncate">{s.secondaryText}</span>
                )}
              </div>
            </li>
          ))}
          {/* Google attribution — required by Maps ToS */}
          <li className="flex justify-end px-3 py-1.5 bg-gray-50 border-t border-gray-100">
            <img src="https://developers.google.com/maps/documentation/images/powered_by_google_on_white.png"
              alt="Powered by Google" className="h-4 object-contain" />
          </li>
        </ul>
      )}

      {/* No results */}
      {open && !loading && inputValue.length >= 2 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-3 text-sm text-gray-400">
          No results found for &ldquo;{inputValue}&rdquo;
        </div>
      )}

    </div>
  );
}